import type { ReactNode } from "react";
import { WarningIcon } from "@phosphor-icons/react/Warning";
import { Tooltip } from "../../Feedback/Tooltip";
import {
  useFormItemStatus,
  useFormItemWarningExplanation,
  useFormItemWarningMessage,
} from "../../FormLayout/FormItemErrorProvider";

/**
 * The in-field warning mark, shared by every control with somewhere to put one.
 * Returns `undefined` unless the surrounding `FormItem` is in a warning state —
 * a hook rather than a component so the caller can still fall through to its
 * other end-slot content, which `<Mark />` couldn't do (JSX is truthy even when
 * the component renders nothing).
 *
 * Only a warning gets a glyph. An error needs none — its border and its message
 * are both already red, whereas a warning's message is plain foreground text, so
 * this mark is the only thing saying "warning".
 *
 * When the `FormItem` also supplied a `warningExplanation`, the mark becomes its
 * trigger and holds it open while the control has focus. It has to be the mark
 * rather than something trailing the message: the message line is one line tall
 * and these fields can be ~72px wide, so a sentence there wraps into whatever
 * sits below.
 *
 * The glyph is sized in `em`, so wrap it in the control's own text size and it
 * matches the value beside it — small in a dense filter bar, large in a
 * spacious quote form.
 */
export function useFormItemWarningMark({
  focused,
  loading,
}: {
  /** Holds the explanation open while the control is being edited. */
  focused?: boolean | undefined;
  /** A spinner already owns the slot; the mark waits its turn. */
  loading?: boolean | undefined;
} = {}): ReactNode | undefined {
  const status = useFormItemStatus();
  const warningMessage = useFormItemWarningMessage();
  const warningExplanation = useFormItemWarningExplanation();

  if (status !== "warning" || loading === true) {
    return undefined;
  }

  const icon = (
    <WarningIcon
      weight="fill"
      aria-hidden={warningExplanation ? undefined : "true"}
      className="size-[1em] shrink-0 text-orange"
    />
  );

  if (warningExplanation === undefined) {
    return icon;
  }

  return (
    <Tooltip
      content={
        <div className="flex flex-col gap-2">
          {/* The triangle again, above the reason. This can open on focus with
              the pointer nowhere near it, so the card has to say what kind of
              thing it is rather than rely on the mark it grew from having been
              noticed. */}
          <span className="flex items-center gap-1.5 font-semibold">
            <WarningIcon
              weight="fill"
              aria-hidden="true"
              className="size-[1.15em] shrink-0 text-orange"
            />
            {warningMessage}
          </span>
          <span>{warningExplanation}</span>
        </div>
      }
      // `top` because this opens while someone is working rather than only when
      // they point at it: `right` covers the next field along, `bottom` covers
      // the control's own warning message. Above is a label already read.
      side="top"
      roomy
      forceOpen={focused}
    >
      <span className="inline-flex cursor-help">{icon}</span>
    </Tooltip>
  );
}
